import { useState, useEffect } from 'react';
import { useAuth } from '@/providers/AuthProvider';
import { notificationService } from '@/services/notificationService';

const FOCUS_ENABLED_KEY = 'notifications-focus-enabled';
const FOCUS_PROFILE_KEY = 'notifications-focus-profile';
const FOCUS_UNTIL_KEY = 'notifications-focus-until';
const SNOOZED_TYPES_KEY = 'notifications-snoozed-types';
const INTELLIGENCE_PROFILE_KEY = 'notifications-intelligence-profile';

type NotificationFocusProfile = 'balanced' | 'urgent-only' | 'social-break';

interface NotificationIntelligenceProfile {
  opensByType: Record<string, number>;
  dismissByType: Record<string, number>;
}

interface Notification {
  id: string;
  userId: string;
  type: string;
  title: string;
  message: string;
  read: boolean;
  createdAt: string;
  readAt?: string;
  urgency?: 'low' | 'medium' | 'high';
  priorityScore?: number;
  collapsedCount?: number;
  data?: {
    postId?: string;
    commentId?: string;
    messageId?: string;
    callId?: string;
    friendshipId?: string;
    senderId?: string;
    senderName?: string;
    senderPhoto?: string;
    senderUsername?: string;
    reactorName?: string;
    reactorPhoto?: string;
    commenterName?: string;
    commenterPhoto?: string;
    authorName?: string;
    authorPhoto?: string;
    authorUsername?: string;
    accepterId?: string;
    accepterName?: string;
    accepterPhoto?: string;
    accepterUsername?: string;
    reactionType?: string;
    commentText?: string;
    actionUrl?: string;
  };
}

export function useNotifications() {
  const [rawNotifications, setRawNotifications] = useState<Notification[]>([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [totalUnreadCount, setTotalUnreadCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [focusEnabled, setFocusEnabled] = useState(false);
  const [focusProfile, setFocusProfile] = useState<NotificationFocusProfile>('balanced');
  const [focusUntil, setFocusUntil] = useState<number | null>(null);
  const [snoozedTypes, setSnoozedTypes] = useState<Record<string, number>>({});
  const [intelligenceProfile, setIntelligenceProfile] = useState<NotificationIntelligenceProfile>({
    opensByType: {},
    dismissByType: {},
  });
  const { user } = useAuth();

  useEffect(() => {
    if (typeof window === 'undefined') {
      return;
    }

    const savedEnabled = window.localStorage.getItem(FOCUS_ENABLED_KEY);
    const savedProfile = window.localStorage.getItem(FOCUS_PROFILE_KEY) as NotificationFocusProfile | null;
    const savedUntilRaw = window.localStorage.getItem(FOCUS_UNTIL_KEY);
    const savedSnoozedRaw = window.localStorage.getItem(SNOOZED_TYPES_KEY);
    const savedIntelligenceRaw = window.localStorage.getItem(INTELLIGENCE_PROFILE_KEY);

    setFocusEnabled(savedEnabled === 'true');
    if (savedProfile === 'balanced' || savedProfile === 'urgent-only' || savedProfile === 'social-break') {
      setFocusProfile(savedProfile);
    }

    if (savedUntilRaw) {
      const parsed = Number(savedUntilRaw);
      if (Number.isFinite(parsed) && parsed > Date.now()) {
        setFocusUntil(parsed);
      }
    }

    if (savedSnoozedRaw) {
      try {
        const parsed = JSON.parse(savedSnoozedRaw) as Record<string, number>;
        const now = Date.now();
        const next: Record<string, number> = {};
        Object.entries(parsed || {}).forEach(([type, until]) => {
          if (Number.isFinite(until) && until > now) {
            next[type] = until;
          }
        });
        setSnoozedTypes(next);
      } catch {
        setSnoozedTypes({});
      }
    }

    if (savedIntelligenceRaw) {
      try {
        const parsed = JSON.parse(savedIntelligenceRaw) as NotificationIntelligenceProfile;
        setIntelligenceProfile({
          opensByType: parsed?.opensByType || {},
          dismissByType: parsed?.dismissByType || {},
        });
      } catch {
        setIntelligenceProfile({ opensByType: {}, dismissByType: {} });
      }
    }
  }, []);

  useEffect(() => {
    if (typeof window === 'undefined') {
      return;
    }
    window.localStorage.setItem(FOCUS_ENABLED_KEY, String(focusEnabled));
  }, [focusEnabled]);

  useEffect(() => {
    if (typeof window === 'undefined') {
      return;
    }
    window.localStorage.setItem(FOCUS_PROFILE_KEY, focusProfile);
  }, [focusProfile]);

  useEffect(() => {
    if (typeof window === 'undefined') {
      return;
    }

    if (focusUntil && focusUntil > Date.now()) {
      window.localStorage.setItem(FOCUS_UNTIL_KEY, String(focusUntil));
      return;
    }

    window.localStorage.removeItem(FOCUS_UNTIL_KEY);
  }, [focusUntil]);

  useEffect(() => {
    if (typeof window === 'undefined') {
      return;
    }

    const now = Date.now();
    const next: Record<string, number> = {};
    Object.entries(snoozedTypes).forEach(([type, until]) => {
      if (Number.isFinite(until) && until > now) {
        next[type] = until;
      }
    });

    window.localStorage.setItem(SNOOZED_TYPES_KEY, JSON.stringify(next));
  }, [snoozedTypes]);

  useEffect(() => {
    if (typeof window === 'undefined') {
      return;
    }

    window.localStorage.setItem(INTELLIGENCE_PROFILE_KEY, JSON.stringify(intelligenceProfile));
  }, [intelligenceProfile]);

  useEffect(() => {
    if (!user) {
      setRawNotifications([]);
      setUnreadCount(0);
      setTotalUnreadCount(0);
      setLoading(false);
      return;
    }

    // Use the notification service to listen to notifications
    const unsubscribe = notificationService.listenToNotifications(
      user.uid,
      (notificationsData) => {
        const nextNotifications = notificationsData as Notification[];
        setRawNotifications(nextNotifications);
        setTotalUnreadCount(nextNotifications.filter((n) => !n.read).length);
        setLoading(false);
      }
    );

    return () => {
      unsubscribe();
    };
  }, [user?.uid]);

  const isSocialType = (type: string) => {
    return ['new_reaction', 'new_comment', 'post_share', 'comment_reply', 'new_post'].includes(type);
  };

  const shouldKeepDuringQuietSession = (notification: Notification) => {
    if (notification.urgency === 'high') {
      return true;
    }

    return ['new_message', 'call', 'friend_request'].includes(notification.type);
  };

  const now = Date.now();
  const isQuietSessionActive = Boolean(focusUntil && focusUntil > now);

  const computeIntelligenceScore = (notification: Notification) => {
    const basePriority = Number(notification.priorityScore || 0);
    const urgencyBoost = notification.urgency === 'high' ? 8 : notification.urgency === 'medium' ? 4 : 1;
    const unreadBoost = notification.read ? 0 : 6;
    const createdAtTs = new Date(notification.createdAt).getTime();
    const ageMinutes = Number.isFinite(createdAtTs) ? Math.max(0, (now - createdAtTs) / (1000 * 60)) : 60;
    const recencyBoost = Math.max(0, 12 - ageMinutes / 10);

    const openAffinity = Number(intelligenceProfile.opensByType[notification.type] || 0);
    const dismissAffinity = Number(intelligenceProfile.dismissByType[notification.type] || 0);
    const behaviorBoost = Math.min(8, openAffinity * 0.6) - Math.min(6, dismissAffinity * 0.5);

    return Math.round(basePriority + urgencyBoost + unreadBoost + recencyBoost + behaviorBoost);
  };

  const notifications = rawNotifications.filter((notification) => {
    const snoozedUntil = Number(snoozedTypes[notification.type] || 0);
    const isSnoozed = Number.isFinite(snoozedUntil) && snoozedUntil > now;
    if (isSnoozed && notification.urgency !== 'high') {
      return false;
    }

    if (!focusEnabled && !isQuietSessionActive) {
      return true;
    }

    if (isQuietSessionActive) {
      return shouldKeepDuringQuietSession(notification);
    }

    if (focusProfile === 'urgent-only') {
      return shouldKeepDuringQuietSession(notification);
    }

    if (focusProfile === 'social-break') {
      if (notification.urgency === 'high') {
        return true;
      }
      return !isSocialType(notification.type);
    }

    if (focusProfile === 'balanced') {
      if (notification.urgency === 'low') {
        const createdAt = new Date(notification.createdAt).getTime();
        if (Number.isFinite(createdAt) && now - createdAt > 24 * 60 * 60 * 1000) {
          return false;
        }
      }
    }

    return true;
  }).map((notification) => ({
    ...notification,
    priorityScore: computeIntelligenceScore(notification),
  })).sort((a, b) => {
    const aReadPenalty = a.read ? -5 : 0;
    const bReadPenalty = b.read ? -5 : 0;
    const byScore = (b.priorityScore || 0) + bReadPenalty - ((a.priorityScore || 0) + aReadPenalty);
    if (byScore !== 0) return byScore;
    return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime();
  });

  useEffect(() => {
    setUnreadCount(notifications.filter((notification) => !notification.read).length);
  }, [notifications]);

  const suppressedCount = Math.max(0, rawNotifications.length - notifications.length);

  const registerNotificationEngagement = (type: string, action: 'open' | 'dismiss') => {
    setIntelligenceProfile((prev) => {
      if (action === 'open') {
        return {
          ...prev,
          opensByType: {
            ...prev.opensByType,
            [type]: Number(prev.opensByType[type] || 0) + 1,
          },
        };
      }

      return {
        ...prev,
        dismissByType: {
          ...prev.dismissByType,
          [type]: Number(prev.dismissByType[type] || 0) + 1,
        },
      };
    });
  };

  const markAsRead = async (notificationId: string) => {
    if (!user) return;
    
    try {
      await notificationService.markAsRead(user.uid, notificationId);
    } catch (error) {
      console.error('Error marking notification as read:', error);
    }
  };

  const markAllAsRead = async () => {
    if (!user) return;
    
    try {
      await notificationService.markAllAsRead(user.uid);
    } catch (error) {
      console.error('Error marking all notifications as read:', error);
    }
  };

  const deleteNotification = async (notificationId: string) => {
    if (!user) return;
    
    try {
      const target = rawNotifications.find((item) => item.id === notificationId);
      if (target) {
        registerNotificationEngagement(target.type, 'dismiss');
      }
      await notificationService.deleteNotification(user.uid, notificationId);
    } catch (error) {
      console.error('Error deleting notification:', error);
    }
  };

  const snoozeNotificationType = (type: string, minutes: number) => {
    const safeMinutes = Math.max(5, Math.min(24 * 60, Math.round(minutes)));
    setSnoozedTypes((prev) => ({
      ...prev,
      [type]: Date.now() + safeMinutes * 60 * 1000,
    }));
    registerNotificationEngagement(type, 'dismiss');
  };

  const clearSnoozeType = (type: string) => {
    setSnoozedTypes((prev) => {
      const next = { ...prev };
      delete next[type];
      return next;
    });
  };

  const clearAllSnoozes = () => {
    setSnoozedTypes({});
  };

  const activeSnoozes = Object.entries(snoozedTypes)
    .filter(([, until]) => Number.isFinite(until) && until > Date.now())
    .map(([type, until]) => ({ type, until }));

  const startFocusSession = (minutes: number) => {
    const duration = Math.max(5, Math.min(240, Math.round(minutes)));
    const until = Date.now() + duration * 60 * 1000;
    setFocusEnabled(true);
    setFocusUntil(until);
  };

  const clearFocusSession = () => {
    setFocusUntil(null);
  };

  return {
    notifications,
    allNotifications: rawNotifications,
    unreadCount,
    totalUnreadCount,
    suppressedCount,
    activeSnoozes,
    loading,
    markAsRead,
    markAllAsRead,
    deleteNotification,
    registerNotificationEngagement,
    snoozeNotificationType,
    clearSnoozeType,
    clearAllSnoozes,
    focusEnabled,
    setFocusEnabled,
    focusProfile,
    setFocusProfile,
    focusUntil,
    isQuietSessionActive,
    startFocusSession,
    clearFocusSession,
  };
}